import { useMemo, useState } from "react";
import { Ban, Minus, Pencil, Plus, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import type { AgentProposal, AgentProposalChange } from "@rebuild/shared/models";
import { AgentProposalChangePath, AgentProposalStatus, AgentProposalToolName } from "@rebuild/shared/models";
import { attemptToStripUnits, type FormulaUnit } from "@rebuild/shared";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useAcceptProposalsMutation, useRejectProposalsMutation } from "@/hooks/mutation/estimate-chat";

interface ProposalCardProps {
  estimateId: string;
  chatId: string;
  // Every proposal the agent made in one assistant turn, in creation order.
  proposals: AgentProposal[];
  // Another user's chat: statuses are visible, nothing is actionable.
  readOnly: boolean;
}

const isPending = (p: AgentProposal) => p.status === AgentProposalStatus.pending;

const isFormulaUpdate = (p: AgentProposal) => p.toolName === AgentProposalToolName.propose_line_item_formula_update;

const isPermutationUpdate = (p: AgentProposal) => p.toolName === AgentProposalToolName.propose_line_item_permutation_update;

const isRoomCreate = (p: AgentProposal) => p.toolName === AgentProposalToolName.propose_room_create;

const isRoomRename = (p: AgentProposal) => p.toolName === AgentProposalToolName.propose_room_rename;

const isLineItemAdd = (p: AgentProposal) => p.toolCallInput.changeType === "lineItemAdd";

const ROOMS_GROUP = "Rooms";

const findChange = (p: AgentProposal, path: AgentProposalChangePath) =>
  p.toolCallInput.changeType === "lineItemChange" ? p.toolCallInput.changes.find((change) => change.path === path) : undefined;

const rowLabel = (p: AgentProposal): string =>
  p.toolCallInput.changeType === "room"
    ? (p.toolCallInput.oldName ?? p.toolCallInput.newName ?? "")
    : p.toolCallInput.description;

// The row shows the unit the way the editor does, as a suffix; the stored old formula may
// already carry it while the new one never does, so strip before appending.
const formatFormulaWithUnit = (formula: AgentProposalChange["oldValue"] | undefined, unit: string | undefined): string => {
  if (typeof formula !== "string" || !formula) return "—";
  if (!unit) return formula;
  return `${attemptToStripUnits(formula, unit as FormulaUnit) ?? formula} ${unit}`;
};

/**
 * Once a row is resolved the marker says what happened to it, in one neutral weight.
 * Failure keeps its colour because it is the only outcome still asking for attention.
 */
function ResolvedMarker({ proposal }: { proposal: AgentProposal }) {
  if (proposal.status === AgentProposalStatus.failed) return <TriangleAlert className="size-4 text-orange-500" />;
  if (proposal.status === AgentProposalStatus.declined) return <Ban className="size-3.5 text-muted-foreground" />;
  if (isFormulaUpdate(proposal) || isPermutationUpdate(proposal) || isRoomRename(proposal)) return <Pencil className="size-3.5 text-muted-foreground" />;
  if (isRoomCreate(proposal) || isLineItemAdd(proposal)) return <Plus className="size-4 text-muted-foreground" />;
  return <Minus className="size-4 text-muted-foreground" />;
}

/** The value going away: struck on red, or muted once the proposal is declined. */
function OldValue({ declined, className, children }: { declined: boolean; className?: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "min-w-0 rounded px-1 line-through",
        "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
        declined && "bg-transparent text-muted-foreground dark:bg-transparent dark:text-muted-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}

function FormulaDiff({
  change,
  unit,
  declined,
}: {
  change: AgentProposalChange | undefined;
  unit: string | undefined;
  declined: boolean;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1 px-1 text-[11px] leading-snug tabular-nums">
      <OldValue declined={declined}>{formatFormulaWithUnit(change?.oldValue, unit)}</OldValue>
      <span aria-hidden="true" className="text-muted-foreground">
        →
      </span>
      <span className="sr-only">changed to</span>
      <span className="min-w-0 px-1 font-medium text-foreground">{formatFormulaWithUnit(change?.newValue, unit)}</span>
    </span>
  );
}

interface ProposalGroup {
  key: string;
  label: string;
  proposals: AgentProposal[];
}

/**
 * Buckets line-item proposals by room, preserving the order they arrived in. Room changes come first,
 * in their own group, matching the order accept applies them. Keys are prefixed so a room that
 * happens to be named like the rooms header stays its own group.
 */
function groupByRoom(proposals: AgentProposal[]): ProposalGroup[] {
  const groups = new Map<string, ProposalGroup>();
  const roomChanges = proposals.filter((p) => p.toolCallInput.changeType === "room");
  if (roomChanges.length) groups.set("rooms", { key: "rooms", label: ROOMS_GROUP, proposals: roomChanges });
  for (const p of proposals) {
    if (p.toolCallInput.changeType === "room") continue;
    const { roomName } = p.toolCallInput;
    const key = `room:${roomName}`;
    const group = groups.get(key) ?? { key, label: roomName, proposals: [] };
    groups.set(key, { ...group, proposals: [...group.proposals, p] });
  }
  return [...groups.values()];
}

function resolvedSummary(proposals: AgentProposal[]): string {
  const counts = [AgentProposalStatus.applied, AgentProposalStatus.declined, AgentProposalStatus.failed]
    .map((status) => [proposals.filter((p) => p.status === status).length, status] as const)
    .filter(([n]) => n > 0);
  if (counts.length === 1) return counts[0][1].charAt(0).toUpperCase() + counts[0][1].slice(1);
  return counts.map(([n, status]) => `${n} ${status}`).join(" · ");
}

export function ProposalCard({ estimateId, chatId, proposals, readOnly }: ProposalCardProps) {
  const acceptProposals = useAcceptProposalsMutation(estimateId, chatId);
  const rejectProposals = useRejectProposalsMutation(estimateId, chatId);
  const isBusy = acceptProposals.isPending || rejectProposals.isPending;

  const pending = useMemo(() => proposals.filter(isPending), [proposals]);
  const groups = useMemo(() => groupByRoom(proposals), [proposals]);

  // Selection is only ever intersected with what is still pending, so a resolved row can
  // never be sent again.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(proposals.map((p) => p.id)));
  const selectedPending = useMemo(() => pending.filter((p) => selected.has(p.id)), [pending, selected]);
  const allSelected = pending.length > 0 && selectedPending.length === pending.length;
  const someSelected = selectedPending.length > 0 && !allSelected;
  const canAct = selectedPending.length > 0 && !isBusy;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pending.map((p) => p.id)));

  const handleConfirm = () => {
    if (!canAct) return;
    acceptProposals.mutate(
      selectedPending.map((p) => p.id),
      {
        onSuccess: ({ outcomes }) => {
          const applied = outcomes.filter((o) => o.status === AgentProposalStatus.applied).length;
          const failed = outcomes.filter((o) => o.status === AgentProposalStatus.failed).length;
          if (applied) toast.success(`Applied ${applied} change${applied === 1 ? "" : "s"}`);
          if (failed) toast.error(`${failed} could not be applied`);
        },
        onError: () => toast.error("Could not apply proposals"),
      },
    );
  };

  const handleDecline = () => {
    if (!canAct) return;
    rejectProposals.mutate(
      selectedPending.map((p) => p.id),
      { onError: () => toast.error("Could not update proposal status to declined.") },
    );
  };

  const anyPending = pending.length > 0;
  const isActionable = anyPending && !readOnly;

  return (
    <div className="rounded-lg border border-border bg-background shadow-sm">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        {isActionable && <Checkbox checked={allSelected} onCheckedChange={toggleAll} disabled={isBusy} aria-label="Select all proposed changes" />}
        <p className="flex-1 text-xs font-medium">
          {proposals.length} proposed change{proposals.length === 1 ? "" : "s"}
        </p>
        {/* Checkbox has no indeterminate state, so partial selection is shown as a count. */}
        {isActionable && someSelected && <span className="text-[11px] leading-4 text-muted-foreground">{selectedPending.length} selected</span>}
      </div>

      <div className="divide-y divide-border">
        {groups.map(({ key, label, proposals: roomProposals }) => (
          <div key={key}>
            <div className="bg-muted/50 px-3 py-1 text-[11px] font-medium text-muted-foreground">{label}</div>
            <ul>
              {roomProposals.map((proposal) => {
                const rowPending = isPending(proposal);
                const input = proposal.toolCallInput;
                const formulaChange = findChange(proposal, AgentProposalChangePath.lineItemformularawFormula);
                const nameChange = findChange(proposal, AgentProposalChangePath.lineItemlineItem);
                const isDeclined = proposal.status === AgentProposalStatus.declined;
                return (
                  <li key={proposal.id} className="flex items-start gap-2 px-3 py-1.5">
                    {rowPending && !readOnly ? (
                      <Checkbox
                        checked={selected.has(proposal.id)}
                        onCheckedChange={() => toggle(proposal.id)}
                        disabled={isBusy}
                        aria-label={`Select proposed change for ${rowLabel(proposal)}`}
                      />
                    ) : (
                      // Slot height matches the label's first line box so the mark centres on it.
                      <span className="flex h-[16.5px] w-4 shrink-0 items-center justify-center">
                        {rowPending ? <Minus className="size-4 text-muted-foreground/40" /> : <ResolvedMarker proposal={proposal} />}
                      </span>
                    )}
                    <span className="flex min-w-0 flex-1 flex-col gap-px">
                      {/* Strikethrough means one thing: this value is going away. Whether it
                          actually did is carried by the marker, never by the text. */}
                      {input.changeType === "room" && input.oldName && !input.newName ? (
                        <OldValue declined={isDeclined} className="text-xs font-medium leading-snug">
                          {input.oldName}
                        </OldValue>
                      ) : input.changeType === "room" && input.oldName ? (
                        <>
                          <OldValue declined={isDeclined} className="text-xs font-medium leading-snug">
                            {input.oldName}
                          </OldValue>
                          <span className="min-w-0 px-1 text-xs font-medium leading-snug">
                            <span className="sr-only">renamed to </span>
                            {input.newName}
                          </span>
                        </>
                      ) : input.changeType === "room" ? (
                        <span className="min-w-0 px-1 text-xs font-medium leading-snug">{input.newName}</span>
                      ) : input.changeType === "lineItemAdd" ? (
                        <>
                          <span className="min-w-0 px-1 text-xs font-medium leading-snug">{input.description}</span>
                          <span className="min-w-0 px-1 text-[11px] font-medium leading-snug tabular-nums text-foreground">
                            {formatFormulaWithUnit(input.formula, input.unit)}
                          </span>
                        </>
                      ) : isFormulaUpdate(proposal) ? (
                        <>
                          <span className="min-w-0 px-1 text-xs font-medium leading-snug">{input.description}</span>
                          <FormulaDiff change={formulaChange} unit={input.unit} declined={isDeclined} />
                        </>
                      ) : isPermutationUpdate(proposal) ? (
                        <>
                          <OldValue declined={isDeclined} className="text-xs font-medium leading-snug">
                            {input.description}
                          </OldValue>
                          <span className="min-w-0 px-1 text-xs font-medium leading-snug">
                            <span className="sr-only">changed to </span>
                            {typeof nameChange?.newValue === "string" ? nameChange.newValue : input.description}
                          </span>
                        </>
                      ) : (
                        <OldValue declined={isDeclined} className="text-xs font-medium leading-snug">
                          {input.description}
                        </OldValue>
                      )}
                      <span className="px-1 text-[11px] leading-snug text-muted-foreground">
                        {proposal.status === AgentProposalStatus.failed && proposal.errorMessage ? proposal.errorMessage : proposal.summary}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* The footer is never removed: a resolved card reports itself at the button's height so
          nothing below it in the conversation reflows. */}
      <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2">
        {isActionable ? (
          <>
            <Button size="sm" variant="ghost" onClick={handleDecline} disabled={!canAct}>
              Decline
            </Button>
            <Button size="sm" onClick={handleConfirm} disabled={!canAct}>
              {acceptProposals.isPending ? "Applying…" : `Confirm ${selectedPending.length} of ${pending.length}`}
            </Button>
          </>
        ) : (
          <span className="flex h-7 items-center text-xs font-medium text-muted-foreground">
            {anyPending ? `${pending.length} awaiting the chat owner` : resolvedSummary(proposals)}
          </span>
        )}
      </div>
    </div>
  );
}
